"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useMsal } from "@azure/msal-react";

import { Country } from "@/types/country";
import {
  ActiveDirectory,
  HelpDeskApiUser,
  HelpDeskContextType,
  HelpDeskUserProfile,
  HelpDeskUserSecurity,
  InstructorTypes,
  Subscriptions,
} from "@/types/helpDesk";
import { GraphUserAuthenticationData } from "@/types/microsoft";
import { AuthenticationMethods } from "@/types/multifactor";
import { UserEmail } from "@/types/user";
import { HelpDeskValidation, ValidationError } from "@/types/validation";

import { getTenantValue } from "@/lib/auth/graph";
import { getPromiseSettledValues, getSharedProperties } from "@/lib/data";
import { httpRequest } from "@/lib/http";

import { useMultifactor } from "@/context/helpDesk/multifactor";
import { useSubscription } from "@/context/helpDesk/subscription";
import { useAuth } from "@/context/util/auth";
import { useUi } from "@/context/util/ui";

import { getGeneralErrorAlert } from "@/components/alerts/global";

const HelpDeskContext = createContext<HelpDeskContextType>(null!);

export function HelpDeskProvider({ children }: { children: ReactNode }) {
  const {
    authorization,
    getAccessToken,
    getAuthenticationMetadata,
    getAvatar,
    msalReady,
  } = useAuth();
  const { alert, loading, setAlert, setLoading, setSnackbar } = useUi();
  const { getAuthenticationMethods } = useMultifactor();
  const { getUserSubscriptions } = useSubscription();

  const router = useRouter();
  const searchParams = useSearchParams();

  const [activeDirectory, setActiveDirectory] =
    useState<ActiveDirectory | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [errors, setErrors] = useState<ValidationError<HelpDeskValidation>>({});
  const [profile, setProfile] = useState<HelpDeskUserProfile | null>(null);
  const [security, setSecurity] = useState<HelpDeskUserSecurity | null>(null);
  const [userEmail, setUserEmail] = useState<UserEmail | null>(null);

  const username = searchParams.get("username");

  useEffect(() => {
    if (!authorization) return;
    if (authorization.hasAuth === false) router.push("/help-desk/unauthorized");
  }, [authorization?.hasAuth, router]);

  useEffect(() => {
    async function getCountries() {
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("No access token available");

        const url = "/account-utilities/api/help-desk/country";

        const headers = { Authorization: `Bearer ${token}` };
        const method = "GET";

        const requestInit: RequestInit = { headers, method };

        const newCountries = (await httpRequest<Country[]>(url, requestInit))
          .data;

        if (!newCountries) throw new Error("Empty http response");

        setCountries(newCountries);
      } catch (error) {
        console.error("Failed to get countries:", error);

        const message =
          "Unable to add or edit phone authentication. If this is needed, please refresh the page and try again.";
        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));
      }
    }

    if (msalReady && !countries.length) getCountries();
  }, [countries, msalReady]);

  const getHelpDeskUser = useCallback(
    async (username: string, firstRun: boolean = false) => {
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("No access token available");

        const url = `/account-utilities/api/help-desk/user?username=${username}`;

        const headers = { Authorization: `Bearer ${token}` };
        const method = "GET";

        const requestInit: RequestInit = { headers, method };

        const user = (await httpRequest<HelpDeskApiUser>(url, requestInit))
          .data;

        if (!user) throw new Error("Empty http response");

        const upn = `${user.username}@${user.domain}`;

        if (firstRun) {
          const message = `${user.firstName} ${user.lastName} data retrieved`;
          setSnackbar((prev) => ({
            ...prev,
            message,
            open: true,
            severity: "success",
            autoHideDuration: 10000,
          }));
        }

        const personalUserEmail = user.emails.find((e) => e.code === "PERS");
        const workUserEmail = user.emails.find((e) => e.code === "WORK");
        const newUserEmail = personalUserEmail ?? workUserEmail ?? null;
        setUserEmail(newUserEmail);

        return { ...user, upn };
      } catch (error) {
        const message = "Failed to get user details. Please try again later.";
        console.error(message, error);

        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 401
        )
          router.push("/help-desk/user/unauthorized");
        else {
          setAlert(getGeneralErrorAlert(message, error, "EM"));

          setSnackbar((prev) => ({
            ...prev,
            message,
            open: true,
            severity: "error",
          }));

          setLoading(false);
        }
      }
    },
    [getAccessToken, setSnackbar],
  );

  const getInstructorTypes = useCallback(
    async (username: string) => {
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("No access token available");

        const baseUrl = `/account-utilities/api/help-desk/instructor`;
        const params = `?username=${username}`;
        const url = `${baseUrl}${params}`;

        const headers = { Authorization: `Bearer ${token}` };
        const method = "GET";

        const requestInit: RequestInit = { headers, method };

        const instructor = (
          await httpRequest<InstructorTypes>(url, requestInit)
        ).data;

        if (!instructor) throw new Error("Empty http response");

        return instructor;
      } catch (error) {
        const message =
          "Failed to get user instructor types. Please try again later.";
        console.error(message, error);

        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));
      }
    },
    [getAccessToken, setSnackbar],
  );

  const getHelpDeskData = useCallback(
    async (username: string, showLoading: boolean = false) => {
      if (!loading) setLoading(true);

      const [getHelpDeskUserResult, getInstructorTypesResult] =
        await Promise.allSettled([
          getHelpDeskUser(username, showLoading),
          getInstructorTypes(username),
        ]);

      const helpDeskApiUser = getPromiseSettledValues<HelpDeskApiUser>(
        getHelpDeskUserResult,
      );

      const instructorTypes = getPromiseSettledValues<InstructorTypes>(
        getInstructorTypesResult,
      );

      if (helpDeskApiUser && instructorTypes) {
        const newProfile = getSharedProperties<
          HelpDeskApiUser,
          HelpDeskUserProfile
        >(helpDeskApiUser);

        const newActiveDirectory = helpDeskApiUser.activeDirectory;

        const newUserSecurity = getSharedProperties<
          HelpDeskApiUser,
          HelpDeskUserSecurity
        >(helpDeskApiUser);

        const upn = helpDeskApiUser.upn;
        const tenant = getTenantValue(newProfile?.userIndicator ?? "EM");

        const [
          getAuthenticationMethodsResult,
          getAuthenticationMetadataResult,
          getAvatarResult,
          getUserSubscriptionsResult,
        ] = await Promise.allSettled([
          getAuthenticationMethods(upn, tenant),
          getAuthenticationMetadata(upn, tenant),
          getAvatar(upn, tenant),
          getUserSubscriptions(upn, tenant),
        ]);

        const authentication = getPromiseSettledValues<AuthenticationMethods>(
          getAuthenticationMethodsResult,
        );

        const authenticationMetadata =
          getPromiseSettledValues<GraphUserAuthenticationData>(
            getAuthenticationMetadataResult,
          );

        const adPhoto = getPromiseSettledValues<string | undefined>(
          getAvatarResult,
        );

        const subscriptions = getPromiseSettledValues<Subscriptions>(
          getUserSubscriptionsResult,
        );

        const onPremPhoto = newActiveDirectory.photo
          ? `data:image/jpeg;base64,` + newActiveDirectory.photo
          : undefined;

        const photo = adPhoto ?? onPremPhoto;

        setProfile(newProfile);

        if (newActiveDirectory) {
          const lastLogon =
            authenticationMetadata?.lastLogon ?? newActiveDirectory?.lastLogon;
          const lastPasswordSet =
            authenticationMetadata?.lastPasswordSet ??
            newActiveDirectory?.lastPasswordSet;

          setActiveDirectory({
            ...newActiveDirectory,
            photo,
            lastLogon,
            lastPasswordSet,
          });

          setSecurity({
            ...newUserSecurity,
            authentication,
            instructorTypes,
            subscriptions,
          });
        } else {
          setActiveDirectory(null);
          setSecurity({
            ...newUserSecurity,
            authentication: undefined,
            subscriptions: undefined,
          });
        }

        if (loading) setLoading(false);
      }
    },
    [getAuthenticationMethods, getHelpDeskUser, getInstructorTypes, loading],
  );

  useEffect(() => {
    if (!authorization) return;
    else if (authorization.hasAuth === false)
      router.push("/help-desk/unauthorized");
    else if (
      authorization?.hasAuth &&
      username &&
      username !== profile?.username
    )
      getHelpDeskData(username as string);
  }, [authorization?.hasAuth, getHelpDeskData, router, username]);

  return (
    <HelpDeskContext.Provider
      value={{
        activeDirectory,
        countries,
        errors,
        profile,
        security,
        setActiveDirectory,
        setErrors,
        setSecurity,
        userEmail,
      }}
    >
      {alert}
      {children}
    </HelpDeskContext.Provider>
  );
}

export function useHelpDesk() {
  const ctx = useContext(HelpDeskContext);
  if (!ctx)
    throw new Error("useHelpDesk must be used within a HelpDeskContext");
  return ctx;
}
